// SPDX-License-Identifier: Elastic-2.0
/**
 * AboutSection — Settings section showing build info + "Reset
 * onboarding" affordance + a couple of external links (T-028).
 *
 * Build info comes from Vite-injected env vars:
 *   - `VITE_MIRLAI_VERSION` (e.g. "0.1.0")
 *   - `VITE_MIRLAI_BUILD`   (e.g. a git short-sha)
 * Either may be undefined in dev; we render `—` in that case so the
 * layout doesn't collapse.
 *
 * Reset onboarding wipes the onboarding store + the "I'm done" flag,
 * then navigates to `/onboarding`. The OnboardingGuard re-evaluates on
 * navigation and sends the user back into A1 from a clean slate.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button";
import { Card } from "@/components/Card/Card";
import { Modal } from "@/components/Modal";
import {
  ONBOARDING_COMPLETE_KEY,
  useOnboardingStore,
} from "@/features/onboarding/useOnboardingStore";

import styles from "./AboutSection.module.css";

/**
 * URL of the public privacy policy. Hard-coded for now — there's no
 * environment override yet; if marketing splits the URL by locale this
 * will move into i18n keys.
 */
const PRIVACY_POLICY_URL = "https://mirlai.com/privacy";

/** URL of the version changelog. Same caveats as above. */
const CHANGELOG_URL = "https://mirlai.com/changelog";

/** Read a Vite env var safely — returns null when missing. */
function readEnv(name: string): string | null {
  const env = (import.meta as ImportMeta).env as Record<string, string | undefined> | undefined;
  const value = env?.[name];
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function AboutSection(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetOnboarding = useOnboardingStore((s) => s.reset);
  const [showConfirm, setShowConfirm] = useState(false);

  const version = readEnv("VITE_MIRLAI_VERSION") ?? "—";
  const build = readEnv("VITE_MIRLAI_BUILD") ?? "—";

  function handleConfirmReset(): void {
    resetOnboarding();
    // `reset()` already clears the complete flag via its internal
    // helper, but we belt-and-braces remove the key here so the
    // guard's next read is deterministic even if the store ever drifts.
    try {
      window.localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
    } catch {
      // Ignore — non-fatal.
    }
    setShowConfirm(false);
    navigate("/onboarding");
  }

  return (
    <Card as="section" variant="elevated" padding="md" data-testid="settings-about">
      <h2 className={styles.title}>{t("settings.about.section_title")}</h2>

      <dl className={styles.fields}>
        <div className={styles.fieldRow}>
          <dt className={styles.fieldLabel}>{t("settings.about.version_label")}</dt>
          <dd className={styles.fieldValue} data-testid="settings-about-version">
            {version}
          </dd>
        </div>
        <div className={styles.fieldRow}>
          <dt className={styles.fieldLabel}>{t("settings.about.build_label")}</dt>
          <dd className={styles.fieldValue} data-testid="settings-about-build">
            {build}
          </dd>
        </div>
      </dl>

      <div className={styles.links}>
        <a
          className={styles.link}
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="settings-about-privacy-policy"
        >
          {t("settings.about.privacy_policy")}
        </a>
        <a
          className={styles.link}
          href={CHANGELOG_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="settings-about-changelog"
        >
          {t("settings.about.changelog")}
        </a>
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="md"
          onClick={() => setShowConfirm(true)}
          data-testid="settings-about-reset-onboarding"
        >
          {t("settings.about.reset_onboarding")}
        </Button>
      </div>

      {/* ADR-003 Amendment §5: transparent disclosure about update infrastructure sourcing. */}
      <p className={styles.disclosure} data-testid="settings-about-disclosure">
        {t("settings.about.update_infrastructure_disclosure")}
      </p>

      <Modal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={t("settings.about.reset_onboarding")}
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="md" onClick={() => setShowConfirm(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleConfirmReset}
              data-testid="settings-about-reset-confirm"
            >
              {t("settings.about.reset_onboarding")}
            </Button>
          </>
        }
      >
        <p>{t("settings.about.reset_confirm")}</p>
      </Modal>
    </Card>
  );
}

export default AboutSection;
